import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sliders, FloppyDisk, Plus, Trash, UsersThree, Clock, Calendar, ChartLine } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import AdminManagement from "@/components/admin/AdminManagement";
import RebaselinePremierEloCard from "@/components/admin/RebaselinePremierEloCard";
import HybridRankingCard from "@/components/admin/HybridRankingCard";
import EloConsistencyCard from "@/components/admin/EloConsistencyCard";
import RollingRebaselineCard from "@/components/admin/RollingRebaselineCard";
import RepairCorruptedEloCard from "@/components/admin/RepairCorruptedEloCard";
import InactivityDecayCard from "@/components/admin/InactivityDecayCard";
import RepairHistoryCard from "@/components/admin/RepairHistoryCard";
import TierLadderCard from "@/components/admin/TierLadderCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettingsTab({ players, pointSettings: initPts, schedSettings: initSched, playerCategories: initCats, seasons: initSeasons, fetchData }) {
  const [pointSettings, setPointSettings] = useState(initPts);
  const [schedSettings, setSchedSettings] = useState(initSched);
  const [playerCategories, setPlayerCategories] = useState(initCats);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [seasonForm, setSeasonForm] = useState({ name: "", start_date: "", end_date: "" });
  const [seasons] = useState(initSeasons);
  const [seasonStats, setSeasonStats] = useState(null);
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);

  const handleSavePointSettings = async () => {
    try {
      await axios.put(`${API}/settings/points`, pointSettings, { withCredentials: true });
      toast.success("Point settings saved!");
    } catch { toast.error("Failed to save settings"); }
  };

  const handleSaveSchedulingSettings = async () => {
    try {
      await axios.put(`${API}/settings/scheduling`, schedSettings, { withCredentials: true });
      toast.success("Scheduling settings saved!");
    } catch { toast.error("Failed to save scheduling settings"); }
  };

  const handleSaveCategories = async () => {
    try {
      await axios.put(`${API}/settings/player-categories`, { categories: playerCategories }, { withCredentials: true });
      toast.success("Player categories saved!");
    } catch { toast.error("Failed to save categories"); }
  };

  const handleAddCategory = () => {
    if (!newCategoryName.trim()) return;
    const slug = newCategoryName.trim().toLowerCase().replace(/\s+/g, '_');
    if (playerCategories.find(c => c.name === slug)) { toast.error("Category already exists"); return; }
    setPlayerCategories([...playerCategories, { name: slug, label: newCategoryName.trim(), max_players: 0, color: "#888888" }]);
    setNewCategoryName("");
  };

  const handleCreateSeason = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/settings/seasons`, seasonForm, { withCredentials: true });
      toast.success("Season created!");
      setSeasonForm({ name: "", start_date: "", end_date: "" });
      fetchData();
    } catch { toast.error("Failed to create season"); }
  };

  const fetchSeasonStats = async (seasonId) => {
    try {
      setSelectedSeasonId(seasonId);
      const res = await axios.get(`${API}/settings/seasons/${seasonId}/stats`);
      setSeasonStats(res.data);
    } catch { toast.error("Failed to load season stats"); }
  };

  const handleArchiveSeason = async (seasonId) => {
    try {
      await axios.put(`${API}/settings/seasons/${seasonId}/archive`, {}, { withCredentials: true });
      toast.success("Season archived");
      fetchData();
    } catch { toast.error("Failed to archive"); }
  };

  return (
    <div className="space-y-6">
      <AdminManagement />

      <RebaselinePremierEloCard />
      <HybridRankingCard />
      <RollingRebaselineCard />
      <TierLadderCard />
      <RepairCorruptedEloCard />
      <RepairHistoryCard />
      <InactivityDecayCard />
      <EloConsistencyCard />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Point Allocation */}
      <Card className="bg-card border-border">
        <CardHeader><CardTitle className="flex items-center gap-2"><Sliders className="w-5 h-5" />Point Allocation</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Winner</Label><Input type="number" value={pointSettings.winner} onChange={e => setPointSettings({ ...pointSettings, winner: parseInt(e.target.value) || 0 })} className="bg-background border-border" /></div>
            <div className="space-y-2"><Label>Runner-up</Label><Input type="number" value={pointSettings.runner_up} onChange={e => setPointSettings({ ...pointSettings, runner_up: parseInt(e.target.value) || 0 })} className="bg-background border-border" /></div>
            <div className="space-y-2"><Label>Semi-final</Label><Input type="number" value={pointSettings.semi_final} onChange={e => setPointSettings({ ...pointSettings, semi_final: parseInt(e.target.value) || 0 })} className="bg-background border-border" /></div>
            <div className="space-y-2"><Label>Quarter-final</Label><Input type="number" value={pointSettings.quarter_final} onChange={e => setPointSettings({ ...pointSettings, quarter_final: parseInt(e.target.value) || 0 })} className="bg-background border-border" /></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2"><Label>Major (x)</Label><Input type="number" step="0.1" value={pointSettings.major_multiplier} onChange={e => setPointSettings({ ...pointSettings, major_multiplier: parseFloat(e.target.value) || 0 })} className="bg-background border-border" /></div>
            <div className="space-y-2"><Label>Monthly (x)</Label><Input type="number" step="0.1" value={pointSettings.monthly_multiplier} onChange={e => setPointSettings({ ...pointSettings, monthly_multiplier: parseFloat(e.target.value) || 0 })} className="bg-background border-border" /></div>
            <div className="space-y-2"><Label>Weekly (x)</Label><Input type="number" step="0.1" value={pointSettings.weekly_multiplier} onChange={e => setPointSettings({ ...pointSettings, weekly_multiplier: parseFloat(e.target.value) || 0 })} className="bg-background border-border" /></div>
          </div>
          <Button onClick={handleSavePointSettings} className="w-full" data-testid="save-points-btn"><FloppyDisk className="w-4 h-4 mr-2" />Save Point Settings</Button>
        </CardContent>
      </Card>

      {/* Player Categories */}
      <Card className="bg-card border-border" data-testid="player-categories-settings">
        <CardHeader><CardTitle className="flex items-center gap-2"><UsersThree className="w-5 h-5 text-secondary" />Player Categories</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {playerCategories.map((cat, idx) => {
            const playersInCat = players.filter(p => p.tier === cat.name);
            return (
              <div key={cat.name} className="p-3 rounded-lg border border-border bg-muted/10 space-y-2" data-testid={`category-${cat.name}`}>
                <div className="flex items-center gap-3">
                  <input type="color" value={cat.color} onChange={e => { const u = [...playerCategories]; u[idx] = { ...u[idx], color: e.target.value }; setPlayerCategories(u); }} className="w-8 h-8 rounded cursor-pointer border-0" />
                  <div className="flex-1"><Input value={cat.label} onChange={e => { const u = [...playerCategories]; u[idx] = { ...u[idx], label: e.target.value }; setPlayerCategories(u); }} className="bg-background border-border h-8 font-medium" data-testid={`category-label-${cat.name}`} /></div>
                  <div className="w-24"><Input type="number" placeholder="Max" value={cat.max_players || ""} onChange={e => { const u = [...playerCategories]; u[idx] = { ...u[idx], max_players: parseInt(e.target.value) || 0 }; setPlayerCategories(u); }} className="bg-background border-border h-8 text-sm" /><p className="text-[10px] text-muted-foreground mt-0.5">Max (0=unlimited)</p></div>
                  {playerCategories.length > 2 && <Button size="sm" variant="ghost" className="h-8 text-destructive" onClick={() => setPlayerCategories(prev => prev.filter((_, i) => i !== idx))}><Trash className="w-3 h-3" /></Button>}
                </div>
                <p className="text-xs text-muted-foreground">{playersInCat.length} player{playersInCat.length !== 1 ? 's' : ''}{cat.max_players > 0 ? ` (max: ${cat.max_players})` : ''}{cat.max_players > 0 && playersInCat.length >= cat.max_players && <span className="text-destructive ml-1 font-semibold">- FULL</span>}</p>
              </div>
            );
          })}
          <div className="flex gap-2">
            <Input value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} placeholder="New category name..." className="bg-background border-border flex-1" data-testid="new-category-input" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddCategory())} />
            <Button variant="outline" onClick={handleAddCategory} data-testid="add-category-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
          </div>
          <Button onClick={handleSaveCategories} className="w-full" data-testid="save-categories-btn"><FloppyDisk className="w-4 h-4 mr-2" />Save Categories</Button>
        </CardContent>
      </Card>

      {/* Scheduling Settings */}
      <Card className="bg-card border-border" data-testid="scheduling-settings">
        <CardHeader><CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5 text-primary" />Scheduling & Tables</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Minutes per Frame</Label><Input type="number" value={schedSettings.mins_per_frame} onChange={e => setSchedSettings({ ...schedSettings, mins_per_frame: parseInt(e.target.value) || 35 })} className="bg-background border-border" data-testid="mins-per-frame-input" /></div>
            <div className="space-y-2"><Label>Buffer (mins)</Label><Input type="number" value={schedSettings.buffer_mins} onChange={e => setSchedSettings({ ...schedSettings, buffer_mins: parseInt(e.target.value) || 10 })} className="bg-background border-border" data-testid="buffer-mins-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Weekday Start</Label><Input type="time" value={schedSettings.weekday_start_time} onChange={e => setSchedSettings({ ...schedSettings, weekday_start_time: e.target.value })} className="bg-background border-border" data-testid="weekday-start-input" /></div>
            <div className="space-y-2"><Label>Weekday End</Label><Input type="time" value={schedSettings.weekday_end_time} onChange={e => setSchedSettings({ ...schedSettings, weekday_end_time: e.target.value })} className="bg-background border-border" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Weekend Start</Label><Input type="time" value={schedSettings.weekend_start_time} onChange={e => setSchedSettings({ ...schedSettings, weekend_start_time: e.target.value })} className="bg-background border-border" /></div>
            <div className="space-y-2"><Label>Weekend End</Label><Input type="time" value={schedSettings.weekend_end_time} onChange={e => setSchedSettings({ ...schedSettings, weekend_end_time: e.target.value })} className="bg-background border-border" /></div>
          </div>
          <div className="space-y-2">
            <Label>Default Tables per Venue</Label>
            <Input type="number" value={schedSettings.default_num_tables} onChange={e => setSchedSettings({ ...schedSettings, default_num_tables: parseInt(e.target.value) || 2 })} className="bg-background border-border" data-testid="default-tables-input" />
          </div>

          {/* NEW: Fixed-slot rule (Feb 2026) — 19:00-22:45 window, BO3 = 112min (2/day/table), BO5+ = 225min (1/day/table) */}
          <div className="pt-2 border-t border-border space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Fixed-slot scheduling</Label>
                <p className="text-[11px] text-muted-foreground">
                  Uses 19:00-22:45 window with fixed per-match slots instead of per-frame math.
                </p>
              </div>
              <Switch
                checked={!!schedSettings.use_fixed_slots}
                onCheckedChange={v => setSchedSettings({ ...schedSettings, use_fixed_slots: v })}
                data-testid="use-fixed-slots-switch"
              />
            </div>
            {schedSettings.use_fixed_slots && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Best-of ≤ 3 slot (mins)</Label>
                  <Input
                    type="number"
                    value={schedSettings.bo3_slot_mins ?? 112}
                    onChange={e => setSchedSettings({ ...schedSettings, bo3_slot_mins: parseInt(e.target.value) || 112 })}
                    className="bg-background border-border"
                    data-testid="bo3-slot-input"
                  />
                  <p className="text-[10px] text-muted-foreground">2 matches/table/day fit in 225-min window</p>
                </div>
                <div className="space-y-2">
                  <Label>Best-of ≥ 5 slot (mins)</Label>
                  <Input
                    type="number"
                    value={schedSettings.long_slot_mins ?? 225}
                    onChange={e => setSchedSettings({ ...schedSettings, long_slot_mins: parseInt(e.target.value) || 225 })}
                    className="bg-background border-border"
                    data-testid="long-slot-input"
                  />
                  <p className="text-[10px] text-muted-foreground">1 match/table/day fills the window</p>
                </div>
              </div>
            )}
          </div>

          <div className="p-3 rounded-lg bg-muted/20 border border-border text-xs text-muted-foreground space-y-1">
            {schedSettings.use_fixed_slots ? (
              <>
                <p>BO≤3 tournament: <strong>{schedSettings.default_num_tables * Math.floor(225 / (schedSettings.bo3_slot_mins || 112))}</strong> matches/day ({schedSettings.default_num_tables} tables × {Math.floor(225 / (schedSettings.bo3_slot_mins || 112))} slots)</p>
                <p>BO≥5 tournament: <strong>{schedSettings.default_num_tables}</strong> matches/day (1 per table)</p>
              </>
            ) : (
              <>
                <p>Bo5 match = {schedSettings.mins_per_frame * 5 + schedSettings.buffer_mins} mins ({schedSettings.mins_per_frame}x5 + {schedSettings.buffer_mins})</p>
                <p>{schedSettings.default_num_tables} tables = {schedSettings.default_num_tables} simultaneous matches</p>
              </>
            )}
          </div>
          <Button onClick={handleSaveSchedulingSettings} className="w-full" data-testid="save-scheduling-btn"><FloppyDisk className="w-4 h-4 mr-2" />Save Scheduling Settings</Button>
        </CardContent>
      </Card>

      {/* Seasons */}
      <Card className="bg-card border-border">
        <CardHeader><CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5" />Seasons</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleCreateSeason} className="space-y-4">
            <Input placeholder="Season Name" value={seasonForm.name} onChange={e => setSeasonForm({ ...seasonForm, name: e.target.value })} className="bg-background border-border" data-testid="season-name-input" />
            <div className="grid grid-cols-2 gap-4">
              <Input type="date" value={seasonForm.start_date} onChange={e => setSeasonForm({ ...seasonForm, start_date: e.target.value })} className="bg-background border-border" data-testid="season-start-date" />
              <Input type="date" value={seasonForm.end_date} onChange={e => setSeasonForm({ ...seasonForm, end_date: e.target.value })} className="bg-background border-border" data-testid="season-end-date" />
            </div>
            <Button type="submit" className="w-full" data-testid="create-season-btn"><Plus className="w-4 h-4 mr-2" />Create Season</Button>
          </form>
          {seasons.length > 0 && (
            <div className="space-y-2 pt-4 border-t border-border">
              <Label>Existing Seasons</Label>
              {seasons.map(s => (
                <div key={s.id} className="p-3 rounded-lg bg-muted/30 border border-border">
                  <div className="flex items-center justify-between mb-1">
                    <div><p className="font-medium">{s.name || "Unnamed"}</p><p className="text-xs text-muted-foreground">{s.start_date} to {s.end_date}</p></div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => fetchSeasonStats(s.id)} data-testid={`season-stats-${s.id}`}><ChartLine className="w-4 h-4" /></Button>
                      {s.is_active ? <Button size="sm" variant="outline" onClick={() => handleArchiveSeason(s.id)} data-testid={`archive-season-${s.id}`}>Archive</Button> : <Badge variant="outline" className="text-muted-foreground">Archived</Badge>}
                    </div>
                  </div>
                  {selectedSeasonId === s.id && seasonStats && (
                    <div className="mt-3 pt-3 border-t border-border space-y-3">
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div className="p-2 rounded bg-primary/10"><p className="text-lg font-bold text-primary">{seasonStats.tournaments?.length || 0}</p><p className="text-[10px] text-muted-foreground">Tournaments</p></div>
                        <div className="p-2 rounded bg-secondary/10"><p className="text-lg font-bold text-secondary">{seasonStats.total_matches}</p><p className="text-[10px] text-muted-foreground">Matches</p></div>
                        <div className="p-2 rounded bg-chart-3/10"><p className="text-lg font-bold text-chart-3">{seasonStats.highest_break}</p><p className="text-[10px] text-muted-foreground">High Break</p></div>
                        <div className="p-2 rounded bg-chart-4/10"><p className="text-lg font-bold text-chart-4">{seasonStats.total_centuries}</p><p className="text-[10px] text-muted-foreground">Centuries</p></div>
                      </div>
                      {seasonStats.leaderboard?.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Season Leaderboard</p>
                          {seasonStats.leaderboard.slice(0, 5).map((p, i) => (
                            <div key={p.player_id} className="flex items-center justify-between text-sm py-1">
                              <span>{i + 1}. {p.name}</span>
                              <span className="text-primary font-medium">{p.wins}W-{p.losses}L</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
